import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, CupSodaIcon, HeartHandshakeIcon, BrainIcon, SparklesIcon } from 'lucide-react';
const HomePage = () => {
  const heroRef = useRef(null);
  useEffect(() => {
    const handleMouseMove = e => {
      if (!heroRef.current) return;
      const {
        clientX,
        clientY
      } = e;
      const {
        width,
        height,
        left,
        top
      } = heroRef.current.getBoundingClientRect();
      const x = (clientX - left) / width;
      const y = (clientY - top) / height;
      heroRef.current.style.setProperty('--mouse-x', x);
      heroRef.current.style.setProperty('--mouse-y', y);
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);
  return <div className="w-full" data-id="element-505">
      {/* Hero Section */}
      <section ref={heroRef} className="relative min-h-screen flex items-center justify-center" style={{
      '--mouse-x': 0.5,
      '--mouse-y': 0.5
    }} data-id="element-506">
        <div className="absolute inset-0 overflow-hidden" data-id="element-507">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/2St8j7dtP7wGSk7uTwFS99/ayira_s_neuro_soul_cafe_cover.jpg" alt="Ayira's Cafe ambiance" className="w-full h-full object-cover" style={{
          transform: 'scale(1.1) translate(calc(var(--mouse-x) * -20px), calc(var(--mouse-y) * -20px))',
          transition: 'transform 0.2s ease-out'
        }} data-id="element-508" />
          <div className="absolute inset-0 bg-black bg-opacity-50" data-id="element-509"></div>
        </div>
        <div className="absolute inset-0 flex flex-col justify-center items-center" data-id="element-510">
          <div className="float-subtle delay-100" data-id="element-511">
            <SparklesIcon size={64} className="text-white opacity-30 absolute -top-16 left-1/4" data-id="element-512" />
          </div>
          <div className="float-subtle delay-300" data-id="element-513">
            <SparklesIcon size={48} className="text-white opacity-20 absolute top-1/4 -right-8" data-id="element-514" />
          </div>
          <div className="float-subtle delay-500" data-id="element-515">
            <SparklesIcon size={32} className="text-white opacity-25 absolute -bottom-8 right-1/3" data-id="element-516" />
          </div>
        </div>
        <div className="relative text-center text-white px-6 max-w-5xl z-10" data-id="element-517">
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-medium mb-6 tracking-tight" data-id="element-518">
            Africa's First Neurodivergent Café
          </h1>
          <p className="text-xl md:text-2xl max-w-2xl mx-auto mb-10 font-light tracking-wide" data-id="element-519">
            Serving Purpose, Not Just Plates
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center" data-id="element-520">
            <a href="tel:+254746210909" className="bg-amber-500 text-white border-2 border-amber-600 px-8 py-3 font-medium transition-all hover:bg-amber-600 hover:shadow-lg" data-id="element-521">
              Make a Reservation
            </a>
            <Link to="/programs" className="bg-white text-gray-900 border-2 border-white px-8 py-3 font-medium transition-all hover:bg-gray-100 hover:shadow-lg" data-id="element-522">
              Explore Our Programs
            </Link>
          </div>
        </div>
        <div className="absolute bottom-10 left-0 right-0 flex justify-center" data-id="element-523">
          <div className="animate-bounce" data-id="element-524">
            <ArrowRightIcon size={24} className="text-white transform rotate-90" data-id="element-525" />
          </div>
        </div>
      </section>
      {/* Vision & Mission */}
      <section className="py-24 px-6 nature-gradient-green" data-id="element-526">
        <div className="container mx-auto max-w-4xl text-center" data-id="element-527">
          <h2 className="text-3xl md:text-4xl font-medium mb-2" data-id="element-528">
            A Movement for Inclusive Change
          </h2>
          <div className="jp-divider asymmetric" data-id="element-529"></div>
          <p className="text-lg text-[var(--color-ink)] mb-16 max-w-3xl mx-auto font-light" data-id="element-530">
            Ayira's Neuro-Soul Café is a soul-led, sensory-friendly café in
            Nairobi that employs, empowers, and celebrates neurodivergent
            individuals. Named after our founder's daughter, we're not just a
            café—we're a movement for inclusive change.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16" data-id="element-531">
            <div className="washi-container p-8 hover:shadow-md transition-shadow duration-300" data-id="element-532">
              <div className="w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-6" data-id="element-533">
                <BrainIcon size={32} className="text-[var(--color-accent-3)]" data-id="element-534" />
              </div>
              <h3 className="text-xl font-medium mb-3" data-id="element-535">Vision</h3>
              <p className="text-[var(--color-ink)] font-light" data-id="element-536">
                A continent where neurodivergent individuals thrive and belong —
                supported by families, schools, and workplaces.
              </p>
            </div>
            <div className="washi-container p-8 hover:shadow-md transition-shadow duration-300" data-id="element-537">
              <div className="w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-6" data-id="element-538">
                <HeartHandshakeIcon size={32} className="text-[var(--color-accent-1)]" data-id="element-539" />
              </div>
              <h3 className="text-xl font-medium mb-3" data-id="element-540">Mission</h3>
              <p className="text-[var(--color-ink)] font-light" data-id="element-541">
                Deliver practical, scalable pathways from diagnosis to
                independence for neurodivergent individuals.
              </p>
            </div>
            <div className="washi-container p-8 hover:shadow-md transition-shadow duration-300" data-id="element-542">
              <div className="w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-6" data-id="element-543">
                <CupSodaIcon size={32} className="text-[var(--color-accent-2)]" data-id="element-544" />
              </div>
              <h3 className="text-xl font-medium mb-3" data-id="element-545">Values</h3>
              <p className="text-[var(--color-ink)] font-light" data-id="element-546">
                Dignity. Equity. Community. Neuro-inclusion.
              </p>
            </div>
          </div>
          <div className="text-center" data-id="element-547">
            <Link to="/about" className="btn-ikigai accent-1" data-id="element-548">
              Learn more about our story
            </Link>
          </div>
        </div>
      </section>
      {/* Competitive Advantage */}
      <section className="py-24 px-6 bg-[var(--color-paper)]" data-id="element-549">
        <div className="container mx-auto max-w-5xl" data-id="element-550">
          <div className="text-center mb-12" data-id="element-551">
            <h2 className="text-3xl md:text-4xl font-medium mb-2" data-id="element-552">
              What Sets Ayira's Apart
            </h2>
            <div className="jp-divider asymmetric" data-id="element-553"></div>
            <p className="text-lg max-w-3xl mx-auto font-light" data-id="element-554">
              We don't just fill a gap. We redefine what inclusion looks like —
              and prove it's possible.
            </p>
          </div>
          <div className="fukinsei-grid mb-12" data-id="element-555">
            <div className="col-span-12 md:col-span-7" data-id="element-556">
              <div className="washi-container h-full overflow-hidden" data-id="element-557">
                <div className="h-48 bg-[var(--color-accent-1)] bg-opacity-5 relative" data-id="element-558">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/2St8j7dtP7wGSk7uTwFS99/ayira_s_neuro_soul_cafe_cover.jpg" alt="Ayira's training program" className="w-full h-full object-cover" data-id="element-559" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent flex items-end p-6" data-id="element-560">
                    <h3 className="text-xl font-medium text-white" data-id="element-561">
                      Training & Employment
                    </h3>
                  </div>
                </div>
                <div className="p-6" data-id="element-562">
                  <p className="text-[var(--color-ink)] mb-4 font-light" data-id="element-563">
                    We believe meaningful work is a right — not a privilege. Our
                    inclusive café offers neurodivergent adults hands-on
                    training in hospitality, service, and essential life skills.
                  </p>
                  <Link to="/programs" className="text-[var(--color-accent-1)] hover:text-[var(--color-accent-1)]/80 font-medium inline-flex items-center" data-id="element-564">
                    Learn more <ArrowRightIcon size={16} className="ml-1" data-id="element-565" />
                  </Link>
                </div>
              </div>
            </div>
            <div className="col-span-12 md:col-span-5 md:mt-16" data-id="element-566">
              <div className="washi-container h-full overflow-hidden" data-id="element-567">
                <div className="h-48 bg-[var(--color-accent-2)] bg-opacity-5 relative" data-id="element-568">
                  <img src="https://joyfultherapy.health/wp-content/uploads/2024/11/bonding-moment-betwen-parent-and-child-683x1024.jpg" alt="Joyful Journeys program" className="w-full h-full object-cover" data-id="element-569" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent flex items-end p-6" data-id="element-570">
                    <h3 className="text-xl font-medium text-white" data-id="element-571">
                      Joyful Journeys
                    </h3>
                  </div>
                </div>
                <div className="p-6" data-id="element-572">
                  <p className="text-[var(--color-ink)] mb-4 font-light" data-id="element-573">
                    Our signature training program equips caregivers, educators,
                    and support staff with practical tools to understand and
                    support neurodivergent children.
                  </p>
                  <Link to="/programs" className="text-[var(--color-accent-2)] hover:text-[var(--color-accent-2)]/80 font-medium inline-flex items-center" data-id="element-574">
                    Learn more <ArrowRightIcon size={16} className="ml-1" data-id="element-575" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
          <div className="text-center" data-id="element-576">
            <Link to="/programs" className="btn-ikigai accent-1" data-id="element-577">
              Explore All Programs
            </Link>
          </div>
        </div>
      </section>
      {/* Media Recognition */}
      <section className="py-24 px-6 nature-gradient-amber" data-id="element-578">
        <div className="container mx-auto max-w-5xl" data-id="element-579">
          <div className="text-center mb-12" data-id="element-580">
            <h2 className="text-3xl md:text-4xl font-medium mb-2" data-id="element-581">
              In Their Words
            </h2>
            <div className="jp-divider asymmetric" data-id="element-582"></div>
          </div>
          <div className="washi-container p-10 mb-16 relative overflow-hidden" data-id="element-583">
            <span className="quote-mark quote-mark-start" data-id="element-584">"</span>
            <div className="max-w-3xl mx-auto text-center" data-id="element-585">
              <p className="media-quote text-2xl mb-8" data-id="element-586">
                Ayira's Café has become renowned for hiring members of staff
                diagnosed with neuro-diverse challenges.
              </p>
              <div className="flex items-center justify-center" data-id="element-587">
                <img src="https://nation.africa/resource/crblob/5006890/89aaa90f751fb9a1e9fb5db060e4424b/dn-logo-svg-data.svg" alt="Nation.co.ke" className="h-8 object-contain mr-4" data-id="element-588" />
                <p className="text-sm font-medium" data-id="element-589">Nation.co.ke</p>
              </div>
            </div>
            <span className="quote-mark quote-mark-end" data-id="element-590">"</span>
          </div>
          <div className="text-center" data-id="element-591">
            <Link to="/media" className="btn-ikigai accent-2" data-id="element-592">
              View Media Coverage
            </Link>
          </div>
        </div>
      </section>
      {/* Future Hub Teaser */}
      <section className="py-24 px-6 bg-[var(--color-paper)]" data-id="element-593">
        <div className="container mx-auto max-w-5xl" data-id="element-594">
          <div className="fukinsei-grid" data-id="element-595">
            <div className="col-span-12 md:col-span-5" data-id="element-596">
              <div className="relative h-full" data-id="element-597">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/o6UMt4pmcsm7hWRsQkg56b/A_visionary_space_for_neurodivergent_inclusion_and_empowerment._Our_upcoming_hub_will_be_a_dedicated_center_advancing_therapy%2C_training%2C_and_employment_for_neurodivergent_individuals_in_africa.jpg" alt="Inclusive Futures Hub concept" className="w-full h-full object-cover" data-id="element-598" />
                <div className="absolute top-4 left-4 bg-[var(--color-accent-2)] text-white px-4 py-1 rounded-none text-sm font-medium" data-id="element-599">
                  Coming Soon
                </div>
              </div>
            </div>
            <div className="col-span-12 md:col-span-7 md:pl-12 flex flex-col justify-center" data-id="element-600">
              <h2 className="text-2xl md:text-3xl font-medium mb-6" data-id="element-601">
                Inclusive Futures Hub
              </h2>
              <p className="text-[var(--color-ink)] mb-8 font-light" data-id="element-602">
                A visionary space for neurodivergent inclusion and empowerment.
                Our upcoming hub will be a dedicated center advancing therapy,
                training, and employment for neurodivergent individuals.
              </p>
              <Link to="/future-hub" className="btn-ikigai accent-1 self-start" data-id="element-603">
                Learn more about our vision
              </Link>
            </div>
          </div>
        </div>
      </section>
      {/* Reservation CTA */}
      <section className="py-24 px-6 nature-gradient-purple" data-id="element-604">
        <div className="container mx-auto max-w-4xl text-center" data-id="element-605">
          <h2 className="text-3xl md:text-4xl font-medium mb-2" data-id="element-606">
            Visit Ayira's Neuro-Soul Café
          </h2>
          <div className="jp-divider asymmetric" data-id="element-607"></div>
          <p className="text-lg text-[var(--color-ink)] mb-10 font-light" data-id="element-608">
            Experience our unique space in Kilimani, Nairobi. Open Thursday to
            Sunday, 12:00 PM - 7:00 PM.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center" data-id="element-609">
            <a href="tel:+254746210909" className="btn-ikigai accent-2" data-id="element-610">
              Call +254 746 210909
            </a>
            <Link to="/visit" className="btn-ikigai accent-1" data-id="element-611">
              Plan Your Visit
            </Link>
          </div>
        </div>
      </section>
    </div>;
};
export default HomePage;